/**
 * Islands and bridges of a hashiwokakero. Pure functions: the TypeScript port of
 * `backend/apps/puzzletypes/bridges/graph.py`, kept at parity by the shared fixtures.
 *
 * The live checks at the bottom are the client's alone (CLAUDE.md §10): they read the islands
 * and the drawn bridges, never the answer key.
 */
import { before, cellKey, pairKey, type Bridge, type Cell } from "./types";

export interface Island {
  cell: Cell;
  want: number;
}

/** Every island and its number, row-major. */
export function islandsOf(grid: string[]): Island[] {
  const found: Island[] = [];
  grid.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const want = Number(row[c]);
      if (/^[1-9]$/.test(row[c])) found.push({ cell: [r, c], want });
    }
  });
  return found;
}

export function islandAt(grid: string[], [r, c]: Cell): number | null {
  const char = grid[r]?.[c];
  return char !== undefined && /^[1-9]$/.test(char) ? Number(char) : null;
}

export type Direction = "L" | "R" | "U" | "D";
const STEP: Record<Direction, [number, number]> = { L: [0, -1], R: [0, 1], U: [-1, 0], D: [1, 0] };

/** The next island from `cell` in a direction, across water only; null at the edge. */
export function nextIsland(grid: string[], cell: Cell, direction: Direction): Cell | null {
  const [dr, dc] = STEP[direction];
  const rows = grid.length;
  const cols = Math.max(0, ...grid.map((row) => row.length));
  let [r, c] = [cell[0] + dr, cell[1] + dc];
  while (r >= 0 && c >= 0 && r < rows && c < cols) {
    if (islandAt(grid, [r, c]) !== null) return [r, c];
    r += dr;
    c += dc;
  }
  return null;
}

/** Every pair of islands a bridge could join: the next island right of, and below, each. */
export function neighbourPairs(grid: string[]): [Cell, Cell][] {
  const pairs: [Cell, Cell][] = [];
  for (const { cell } of islandsOf(grid))
    for (const direction of ["R", "D"] as const) {
      const other = nextIsland(grid, cell, direction);
      if (other) pairs.push([cell, other]);
    }
  return pairs;
}

/** The cells a bridge from `a` to `b` spans, ends excluded. */
export function waterBetween(a: Cell, b: Cell): Cell[] {
  const cells: Cell[] = [];
  if (a[0] === b[0])
    for (let c = Math.min(a[1], b[1]) + 1; c < Math.max(a[1], b[1]); c++) cells.push([a[0], c]);
  else for (let r = Math.min(a[0], b[0]) + 1; r < Math.max(a[0], b[0]); r++) cells.push([r, a[1]]);
  return cells;
}

export function isHorizontal(a: Cell, b: Cell): boolean {
  return a[0] === b[0];
}

export type Misplaced = "bridge_not_between_islands" | "duplicate_bridge";

/** Bridges that are not a legal pair of neighbouring islands, as `[index, code]` (as Python). */
export function misplacedBridges(grid: string[], bridges: Bridge[]): [number, Misplaced][] {
  const legal = new Set(neighbourPairs(grid).map(([a, b]) => `${cellKey(a)}-${cellKey(b)}`));
  const seen = new Set<string>();
  const found: [number, Misplaced][] = [];
  bridges.forEach(({ a, b }, index) => {
    const key = `${cellKey(a)}-${cellKey(b)}`;
    if (!legal.has(key)) found.push([index, "bridge_not_between_islands"]);
    else if (seen.has(key)) found.push([index, "duplicate_bridge"]);
    seen.add(key);
  });
  return found;
}

/** Whether two bridges cross: one horizontal, one vertical, over a common water cell. */
function crosses(x: Bridge, y: Bridge): boolean {
  if (isHorizontal(x.a, x.b) === isHorizontal(y.a, y.b)) return false;
  const cells = new Set(waterBetween(x.a, x.b).map(cellKey));
  return waterBetween(y.a, y.b).some((cell) => cells.has(cellKey(cell)));
}

/** Indices of bridges that cross another one, ascending. */
export function crossingBridges(bridges: Bridge[]): number[] {
  const found = new Set<number>();
  for (let i = 0; i < bridges.length; i++)
    for (let j = i + 1; j < bridges.length; j++)
      if (crosses(bridges[i], bridges[j])) {
        found.add(i);
        found.add(j);
      }
  return [...found].sort((x, y) => x - y);
}

export function bridgeCounts(bridges: Bridge[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const { a, b, n } of bridges)
    for (const end of [a, b]) counts.set(cellKey(end), (counts.get(cellKey(end)) ?? 0) + n);
  return counts;
}

/** Islands whose bridges do not add up to their number, row-major. */
export function countMismatches(grid: string[], bridges: Bridge[]): Cell[] {
  const counts = bridgeCounts(bridges);
  return islandsOf(grid)
    .filter(({ cell, want }) => (counts.get(cellKey(cell)) ?? 0) !== want)
    .map(({ cell }) => cell);
}

/** The islands grouped by the bridges that join them, each group row-major. */
export function groups(grid: string[], bridges: Bridge[]): Cell[][] {
  const islands = islandsOf(grid);
  const links = new Map<string, Cell[]>(islands.map(({ cell }) => [cellKey(cell), []]));
  for (const { a, b } of bridges) {
    const la = links.get(cellKey(a));
    const lb = links.get(cellKey(b));
    if (la && lb) {
      la.push(b);
      lb.push(a);
    }
  }
  const seen = new Set<string>();
  const found: Cell[][] = [];
  for (const { cell } of islands) {
    if (seen.has(cellKey(cell))) continue;
    const group: Cell[] = [];
    const stack = [cell];
    seen.add(cellKey(cell));
    while (stack.length > 0) {
      const current = stack.pop() as Cell;
      group.push(current);
      for (const other of links.get(cellKey(current)) ?? [])
        if (!seen.has(cellKey(other))) {
          seen.add(cellKey(other));
          stack.push(other);
        }
    }
    found.push(group.sort((x, y) => (before(x, y) ? -1 : 1)));
  }
  return found;
}

/** Whether the bridges join every island into one group. */
export function allConnected(grid: string[], bridges: Bridge[]): boolean {
  return groups(grid, bridges).length <= 1;
}

/** The bridges sorted by their ends: the submission's canonical form. */
export function canonical(bridges: Bridge[]): Bridge[] {
  return [...bridges]
    .map(({ a, b, n }) => ({ a: [a[0], a[1]] as Cell, b: [b[0], b[1]] as Cell, n }))
    .sort((x, y) => {
      if (x.a[0] !== y.a[0]) return x.a[0] - y.a[0];
      if (x.a[1] !== y.a[1]) return x.a[1] - y.a[1];
      if (x.b[0] !== y.b[0]) return x.b[0] - y.b[0];
      return x.b[1] - y.b[1];
    });
}

/** The verdict, client side: legal, uncrossed, every number met, one group. */
export function isSolved(grid: string[], bridges: Bridge[]): boolean {
  return (
    misplacedBridges(grid, bridges).length === 0 &&
    crossingBridges(bridges).length === 0 &&
    countMismatches(grid, bridges).length === 0 &&
    allConnected(grid, bridges)
  );
}

/**
 * Reads any list as legal bridges of this grid: each end an island, the pair neighbours,
 * `a` first in reading order, `n` 1 or 2, a pair once. Whatever does not fit is dropped.
 */
export function legalBridges(grid: string[], raw: unknown): Bridge[] {
  if (!Array.isArray(raw)) return [];
  const legal = new Set(neighbourPairs(grid).map(([a, b]) => pairKey(a, b)));
  const seen = new Set<string>();
  const found: Bridge[] = [];
  const cellOf = (x: unknown): Cell | null =>
    Array.isArray(x) && x.length === 2 && x.every((v) => Number.isInteger(v))
      ? [x[0] as number, x[1] as number]
      : null;
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const { a, b, n } = item as { a?: unknown; b?: unknown; n?: unknown };
    const first = cellOf(a);
    const second = cellOf(b);
    if (!first || !second || (n !== 1 && n !== 2)) continue;
    const key = pairKey(first, second);
    if (!legal.has(key) || seen.has(key)) continue;
    seen.add(key);
    found.push(before(first, second) ? { a: first, b: second, n } : { a: second, b: first, n });
  }
  return found;
}

/** Every number the bridges give their islands; an island without a bridge keeps its own. */
export function islandsNumberedBy(grid: string[], bridges: Bridge[]): string[] {
  const counts = bridgeCounts(bridges);
  return grid.map((row, r) =>
    Array.from(row, (char, c) => {
      const count = counts.get(cellKey([r, c])) ?? 0;
      return /^[1-9]$/.test(char) && count > 0 && count <= 8 ? String(count) : char;
    }).join(""),
  );
}

// ---- live feedback ------------------------------------------------------------------------

export interface Contradictions {
  /** Islands with more bridges than their number. */
  over: Cell[];
  /** Islands that can no longer reach their number, whatever bridges are added. */
  short: Cell[];
  /** Indices, in the given list, of bridges that cross another one. */
  crossing: number[];
  /** Islands of a group that is complete, closed on itself, while other islands remain. */
  isolated: Cell[];
}

/**
 * What can no longer lead to a solution, whatever bridges are added. Sound, not complete:
 * anything that might still work out is left alone (CLAUDE.md §16.4).
 */
export function contradictions(grid: string[], bridges: Bridge[]): Contradictions {
  const islands = islandsOf(grid);
  const wanted = new Map(islands.map(({ cell, want }) => [cellKey(cell), want]));
  const counts = bridgeCounts(bridges);
  const count = (cell: Cell) => counts.get(cellKey(cell)) ?? 0;
  const drawn = new Map(bridges.map((bridge) => [pairKey(bridge.a, bridge.b), bridge]));

  const over = islands.filter(({ cell, want }) => count(cell) > want).map(({ cell }) => cell);

  const pairs = neighbourPairs(grid);
  const short = islands
    .filter(({ cell, want }) => {
      if (count(cell) >= want) return false;
      let room = 0;
      for (const [a, b] of pairs) {
        const other = cellKey(a) === cellKey(cell) ? b : cellKey(b) === cellKey(cell) ? a : null;
        if (!other) continue;
        const key = pairKey(a, b);
        const candidate: Bridge = { a, b, n: 1 };
        // A pair another bridge already crosses cannot take one more.
        if (bridges.some((x) => pairKey(x.a, x.b) !== key && crosses(x, candidate))) continue;
        const already = drawn.get(key)?.n ?? 0;
        room += Math.min(
          2 - already,
          Math.max(0, (wanted.get(cellKey(other)) ?? 0) - count(other)),
        );
      }
      return count(cell) + room < want;
    })
    .map(({ cell }) => cell);

  const crossing = crossingBridges(bridges);

  const all = groups(grid, bridges);
  const isolated =
    all.length > 1
      ? all
          .filter((group) => group.every((cell) => count(cell) === wanted.get(cellKey(cell))))
          .flat()
          .sort((x, y) => (before(x, y) ? -1 : 1))
      : [];

  return { over, short, crossing, isolated };
}

/** Islands whose number is met, for the dimmed look. Not a verdict: just a count. */
export function satisfiedIslands(grid: string[], bridges: Bridge[]): Cell[] {
  const counts = bridgeCounts(bridges);
  return islandsOf(grid)
    .filter(({ cell, want }) => (counts.get(cellKey(cell)) ?? 0) === want)
    .map(({ cell }) => cell);
}

/**
 * Where the keyboard cursor goes from `cell` in a direction: the next island that way, else
 * the nearest island anywhere on that side, so every island stays reachable.
 */
export function islandToward(grid: string[], cell: Cell, direction: Direction): Cell | null {
  const straight = nextIsland(grid, cell, direction);
  if (straight) return straight;
  const [dr, dc] = STEP[direction];
  let best: Cell | null = null;
  let bestScore = Infinity;
  for (const { cell: other } of islandsOf(grid)) {
    const along = (other[0] - cell[0]) * dr + (other[1] - cell[1]) * dc;
    if (along <= 0) continue;
    const across = Math.abs((other[0] - cell[0]) * dc) + Math.abs((other[1] - cell[1]) * dr);
    const score = along + 2 * across;
    if (score < bestScore) {
      best = other;
      bestScore = score;
    }
  }
  return best;
}
